"use client";

/**
 * shell.tsx — 道场导航壳(灯下道场令牌,与 /train 同调)
 *
 * 4 个主门(训练/课库/题库/我的进展)+ 次级一行(场景库·教练)+
 * 生产管理收底部折叠(保留一键可达与展开记忆)。logo 回「/」(牌桌)。
 * ≤560px 视口:侧栏折叠为顶部图标条(常驻高 56px,主内容占满屏宽);
 * >560px 桌面态 w-56 侧栏原样(#933e,常驻闸 probe/viewport-nav)。
 */

import { useEffect, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  BarChart3,
  BookOpen,
  ChevronDown,
  Clapperboard,
  Dumbbell,
  FolderKanban,
  GraduationCap,
  Link2,
  MessageSquare,
  Scissors,
  Settings2,
  Spade,
  Users,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { DISPLAY_FONT } from "@/components/dojo-theme";

const OPS_KEY = "dojo-ops-open";
const opsListeners = new Set<() => void>();

function subscribeOps(cb: () => void): () => void {
  opsListeners.add(cb);
  const onStorage = () => cb();
  window.addEventListener("storage", onStorage);
  return () => {
    opsListeners.delete(cb);
    window.removeEventListener("storage", onStorage);
  };
}

function getOpsSnapshot(): boolean {
  try {
    return localStorage.getItem(OPS_KEY) === "1";
  } catch {
    return false;
  }
}

function toggleOpsOpen(): void {
  try {
    localStorage.setItem(OPS_KEY, localStorage.getItem(OPS_KEY) === "1" ? "0" : "1");
  } catch {
    /* 隐私模式 localStorage 不可用,静默忽略 */
  }
  opsListeners.forEach((cb) => cb());
}

const NAV_MAIN = [
  { href: "/train", label: "训练", icon: Dumbbell },
  { href: "/lessons", label: "课库", icon: BookOpen },
  { href: "/quiz", label: "题库", icon: GraduationCap },
  { href: "/stats", label: "我的进展", icon: BarChart3 },
];

/** 次级入口:低视觉权重,但始终可达(不从导航删页面) */
const NAV_MORE = [
  { href: "/scenes", label: "场景库", icon: Spade },
  { href: "/coach", label: "教练", icon: MessageSquare },
];

const NAV_OPS = [
  { href: "/library", label: "素材库", icon: Clapperboard },
  { href: "/clips", label: "切片包", icon: Scissors },
  { href: "/authors", label: "账号", icon: Users },
  { href: "/ingest", label: "提交链接", icon: Link2 },
  { href: "/jobs", label: "任务中心", icon: FolderKanban },
];

/**
 * 学习者屏清单(宪法 §6:素材库/生产管理仅作生产侧入口,不示于学习者)。
 * 学习者路由不渲「生产管理」段(含「素材 N」计数徽标)——生产痕迹对学习者无教学意义;
 * 生产路由(ops 各页)上该段照常可达。r1 评审 learner_surface_purity 阻塞收口。
 */
const LEARNER_PREFIXES = ["/train", "/lessons", "/quiz", "/stats", "/coach", "/scenes"];

function isLearnerRoute(pathname: string): boolean {
  if (pathname === "/") return true;
  return LEARNER_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function NavLink({
  href,
  label,
  icon: Icon,
  pathname,
  compact,
}: {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  pathname: string;
  compact?: boolean;
}) {
  const active = href === "/" ? pathname === "/" : pathname.startsWith(href);
  return (
    <Link
      href={href}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex items-center gap-2.5 rounded-lg font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70",
        compact ? "px-2.5 py-1.5 text-[13px]" : "px-3 py-2 text-sm",
        active
          ? "bg-emerald-400/10 text-emerald-200 ring-1 ring-inset ring-emerald-400/25"
          : "text-stone-400 hover:bg-white/[0.04] hover:text-stone-100",
      )}
    >
      <Icon className={cn("size-4", active ? "text-emerald-300" : "opacity-70")} />
      <span className="max-[560px]:hidden">{label}</span>
    </Link>
  );
}

export function Shell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const learner = isLearnerRoute(pathname);
  const opsActive = NAV_OPS.some((item) => pathname.startsWith(item.href));
  const opsOpen = useSyncExternalStore(subscribeOps, getOpsSnapshot, () => false);
  const [assetsCount, setAssetsCount] = useState<number | null>(null);

  useEffect(() => {
    if (learner) return;
    fetch("/api/catalog", { cache: "no-store" })
      .then((r) => r.json())
      .then(
        (data: {
          items?: Array<{ isTeaching?: boolean; contentType?: string }>;
        }) => {
          const n = (data.items ?? []).filter(
            (i) => i.isTeaching || String(i.contentType ?? "").startsWith("teaching"),
          ).length;
          setAssetsCount(n);
        },
      )
      .catch(() => {
        /* 拿不到计数,只显示「生产管理」 */
      });
  }, [learner]);

  return (
    <div className="flex min-h-screen flex-col bg-[#090E0B] text-stone-100 min-[561px]:flex-row">
      {/* ≤560px = 顶部图标条(h-14 常驻高 56px,横滚自救);>560px = w-56 侧栏原样 */}
      <aside className="relative flex h-14 shrink-0 flex-row items-center gap-1 overflow-x-auto border-b border-[#1E2E23] bg-[#0A0F0C] px-2 min-[561px]:h-auto min-[561px]:w-56 min-[561px]:flex-col min-[561px]:items-stretch min-[561px]:gap-0 min-[561px]:overflow-visible min-[561px]:border-b-0 min-[561px]:border-r min-[561px]:p-4">
        {/* 侧栏头顶的一线灯光,与主区同一盏灯 */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 hidden h-44 bg-[radial-gradient(ellipse_95%_100%_at_50%_-24%,rgba(228,184,91,0.09),transparent_70%)] min-[561px]:block"
        />

        {/* 品牌:道场令牌(暖金黑桃),点它回牌桌 */}
        <Link
          href="/"
          aria-label="GTO 道场 · 回首页"
          className="group relative flex items-center gap-2.5 rounded-lg px-1 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
        >
          <span
            aria-hidden
            className="flex size-8 items-center justify-center rounded-lg bg-[radial-gradient(circle_at_35%_28%,#F7DFA6,#E3B55B_58%,#8A6420)] shadow-[0_0_22px_-4px_rgba(227,181,91,0.55)] ring-1 ring-amber-200/40 transition-shadow duration-200 group-hover:shadow-[0_0_28px_-2px_rgba(227,181,91,0.7)]"
          >
            <Spade className="size-3.5 text-[#2A1D04]" fill="currentColor" strokeWidth={0} />
          </span>
          <span className="hidden min-[561px]:block">
            <span
              className={cn(
                DISPLAY_FONT,
                "block text-base font-extrabold leading-tight tracking-tight text-stone-50",
              )}
            >
              GTO 道场
            </span>
            <span className="block text-[10px] tracking-wide text-stone-500">
              灯下练牌
            </span>
          </span>
        </Link>

        <nav className="relative flex flex-row items-center gap-1 min-[561px]:mt-7 min-[561px]:flex-1 min-[561px]:flex-col min-[561px]:items-stretch">
          {NAV_MAIN.map((item) => (
            <NavLink key={item.href} {...item} pathname={pathname} />
          ))}

          {/* 次级入口:主门下的一行小字 */}
          <div className="ml-1 flex items-center border-l border-[#1E2E23]/80 pl-1 min-[561px]:ml-0 min-[561px]:mt-4 min-[561px]:block min-[561px]:border-l-0 min-[561px]:border-t min-[561px]:pl-0 min-[561px]:pt-3">
            <div className="flex items-center gap-1 px-2 text-xs">
              {NAV_MORE.map((item, i) => {
                const active = pathname.startsWith(item.href);
                const Icon = item.icon;
                return (
                  <span key={item.href} className="flex items-center gap-1">
                    {i > 0 && (
                      <span aria-hidden className="text-stone-700">
                        ·
                      </span>
                    )}
                    <Link
                      href={item.href}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70",
                        active
                          ? "text-emerald-300"
                          : "text-stone-500 hover:text-stone-200",
                      )}
                    >
                      <Icon className="size-3.5" />
                      {item.label}
                    </Link>
                  </span>
                );
              })}
            </div>
          </div>
        </nav>

        {/* 生产管理:收底部,一键可达,进相关页自动展开;学习者屏不渲(宪法 §6) */}
        {!learner && (
        <div className="relative ml-1 flex items-center border-l border-[#1E2E23]/80 pl-1 min-[561px]:ml-0 min-[561px]:block min-[561px]:border-l-0 min-[561px]:border-t min-[561px]:pl-0 min-[561px]:pt-3">
          <button
            type="button"
            aria-expanded={opsOpen}
            onClick={toggleOpsOpen}
            className={cn(
              "flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70 max-[560px]:w-auto",
              opsActive
                ? "text-stone-300"
                : "text-stone-600 hover:text-stone-400",
            )}
          >
            <Settings2 className="size-3.5 opacity-70" />
            <span className="flex-1 text-left font-medium tracking-wide">
              生产管理
              {assetsCount !== null && (
                <span className="ml-1.5 font-mono text-[10px] tabular-nums text-stone-500">
                  · 素材 {assetsCount}
                </span>
              )}
            </span>
            <ChevronDown
              className={cn(
                "size-3.5 transition-transform duration-200",
                opsOpen && "rotate-180",
              )}
            />
          </button>
          {opsOpen && (
            <div className="mt-1 space-y-0.5 pl-1">
              {NAV_OPS.map((item) => (
                <NavLink key={item.href} {...item} pathname={pathname} compact />
              ))}
            </div>
          )}
        </div>
        )}
      </aside>
      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-6xl p-6 md:p-8">{children}</div>
      </main>
    </div>
  );
}
